/**
 * Collapse a burst of triggers into ONE call on the next animation frame
 * (ADO #104914).
 *
 * WHY THIS EXISTS. The Claim Owner capacity band is refreshed by rewriting
 * every mounted host in place, and that refresh is wired to three Bryntum
 * events — `rowExpand`, `timeAxisChange` and `dateRangeChange` — each of which
 * fires many times per dispatcher action:
 *
 *  - the toolbar's bulk Expand calls `RowExpander#expand` once per resource, so
 *    a 40-resource board fires `rowExpand` 40 times and each pass rewrites all
 *    ~40 hosts — quadratic in the number of resources for ONE click;
 *  - a single zoom gesture fires `timeAxisChange` repeatedly, and the one-week
 *    clamp's `setTimeSpan` correction re-fires `dateRangeChange` on top of it.
 *
 * WHY A WRAPPER AND NOT A CHANGE TO THE REFRESH. `refreshCapacityBands` is
 * deliberately a full, synchronous, in-place rewrite (RowExpander caches its
 * body element per record, so collapse/re-expand reuses stale DOM and only an
 * in-place rewrite refreshes it). Coalescing is a scheduling concern, so it is
 * layered ON one shared callback rather than baked into the renderer — which
 * also keeps `refreshCapacityBands` directly callable and synchronously
 * testable.
 *
 * THE LAST STATE ALWAYS WINS. This is a TRAILING-edge coalesce: the very first
 * trigger schedules a frame, every trigger inside that frame is absorbed, and
 * the wrapped callback runs AFTER the burst has finished. Because the callback
 * reads its inputs (the axis geometry, the capacity map) when it runs rather
 * than when it was queued, the single pass observes the final state — nothing
 * is dropped and nothing is rendered from a stale snapshot. That property is
 * the contract: only wrap a callback that re-reads live state and takes no
 * arguments. A leading-edge throttle would break it by rendering the FIRST
 * state and swallowing the last.
 *
 * The pending handle is cleared BEFORE the callback runs, so a trigger raised
 * from inside the callback queues a fresh frame instead of being silently
 * absorbed by a handle that is about to be discarded.
 */

/** A coalesced callback: call it to request a pass, `cancel()` to drop one. */
export interface CoalescedCallback {
  (): void;
  /**
   * Drop any pass that has not run yet. Required on unmount: a frame queued
   * against a torn-down board would otherwise run against dead DOM.
   */
  cancel(): void;
}

/**
 * `requestAnimationFrame` is read at schedule time, not at wrap time, so a test
 * that installs fake timers (or a spy) after the wrapper was created still
 * governs when the pass runs. `setTimeout` is the fallback for any environment
 * without rAF — the coalescing contract holds either way.
 */
export function coalesceFrame(run: () => void): CoalescedCallback {
  let handle: number | null = null;
  let pendingIsTimeout = false;

  const cancel = () => {
    if (handle === null) return;
    if (pendingIsTimeout) {
      clearTimeout(handle);
    } else {
      globalThis.cancelAnimationFrame(handle);
    }
    handle = null;
  };

  const schedule = () => {
    // A pass is already queued. It has not run yet, so it will read whatever
    // this trigger just changed — absorbing the trigger loses nothing.
    if (handle !== null) return;

    const fire = () => {
      handle = null;
      run();
    };

    const raf = globalThis.requestAnimationFrame;
    pendingIsTimeout = typeof raf !== 'function';
    handle = pendingIsTimeout
      ? (setTimeout(fire, 0) as unknown as number)
      : raf.call(globalThis, fire);
  };

  schedule.cancel = cancel;
  return schedule;
}
